import { ReactNode, useEffect, useMemo } from 'react';
import { SetURLSearchParams, useSearchParams } from 'react-router-dom';
import { isEqual } from 'lodash';

import { Maybe } from '#/utils/types';
import createSafeContext from '#/utils/createSafeContext';

import { updateSearchParam } from './utils';

const ACTIVE_TABS = new Set([
  'collateral',
  'positions',
  'open-orders',
  'trade-history',
  'order-history',
  'funding-payments',
  'deposit-withdraw',
] as const);

interface Actions {
  readonly updateActiveTab: (value: string | null) => void;
  readonly reset: () => void;
}

export type PortfolioSearchParams = {
  readonly active_tab: typeof ACTIVE_TABS extends Set<infer T> ? T : never;
};

const INITIAL_STATE: PortfolioSearchParams = { active_tab: 'collateral' };

export const [usePortfolioSearchParams, StateProvider] =
  createSafeContext<PortfolioSearchParams>('PortfolioSearchParamsState');

export const [usePortfolioSearchParamsActions, ActionsProvider] =
  createSafeContext<Actions>('PortfolioSearchParamsActions');

interface Props {
  readonly children: ReactNode;
}

export const PortfolioSearchParamsProvider = ({ children }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => createState(searchParams), [searchParams]);
  const actions = useMemo(
    () => createActions(setSearchParams),
    [setSearchParams],
  );

  useURLSearchParamsUpdater(searchParams, setSearchParams, state);

  return (
    <StateProvider value={state}>
      <ActionsProvider value={actions}>{children}</ActionsProvider>
    </StateProvider>
  );
};

function createActions(setSearchParams: SetURLSearchParams): Actions {
  return {
    updateActiveTab(value) {
      setSearchParams((searchParams) => {
        return updateSearchParam(searchParams, 'active_tab', String(value));
      });
    },
    reset() {
      setSearchParams(INITIAL_STATE);
    },
  };
}

function createState(urlSearchParams: URLSearchParams): PortfolioSearchParams {
  const maybeActiveTab = urlSearchParams.get('active_tab') as Maybe<
    PortfolioSearchParams['active_tab']
  >;

  const active_tab = activeTabState(maybeActiveTab);

  return {
    active_tab,
  };
}

function activeTabState(
  value: Maybe<PortfolioSearchParams['active_tab']>,
): PortfolioSearchParams['active_tab'] {
  if (value == null) return INITIAL_STATE['active_tab'];
  if (!ACTIVE_TABS.has(value)) return INITIAL_STATE['active_tab'];

  return value;
}

/**
 * Updates the URL search parameters if the URL search parameters get out of sync with the state.
 *  This can happen if URL contains invalid values.
 */
function useURLSearchParamsUpdater(
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams,
  state: PortfolioSearchParams,
) {
  useEffect(() => {
    const urlState = Object.fromEntries(searchParams.entries());
    if (!isEqual(urlState, state)) {
      setSearchParams(state);
    }
  }, [searchParams, setSearchParams, state]);
}
